import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/pro-regular-svg-icons'
import { LabraToolTipV2 } from '@labrav/react-components'
import { get, isEmpty, toUpper } from 'lodash'
import { dateAndTimeConversion } from '../../common/utils/dateAndTimeConversion'
import type { DetailedOffer } from '../PrivateOfferForm/DetailedOffer/DetailedOffer'
import { AdditionalCPPODataKeys } from '../PrivateOfferForm/DetailedOffer/utils'
import {
  ContractDurationType,
  DiscountType,
  ResellerAgreementStatus,
} from '../../types/flyout'
import type { CloudMarketplace } from '../../types/index'
import type { ResaleAuthorizationsResellerAgreements } from '../../types/resaleAuthorization'
import { getDeleteResellerDisabledTooltip } from '../ResellerAuthorizations/ViewDetailsResellerAuthorization/ResellerAuthorizationHeader/helpers'
import { Marketplace } from './constants'

export const getIsCustomPricing = (
  discountType?: DiscountType,
  contractDurationType?: ContractDurationType
) =>
  toUpper(discountType) === DiscountType.INDIVIDUAL_PRICING &&
  contractDurationType === ContractDurationType.CUSTOM

export const getResaleOfferForDetailedView = (
  agreement: ResaleAuthorizationsResellerAgreements,
  timeZone: string
) => {
  const { modifiedDate: agreementExpirationDate } = dateAndTimeConversion(
    agreement.agreementExpirationDate as string,
    timeZone
  )
  const offer = {
    dimensions: get(agreement, 'products[0].dimensions', []),
    installments: agreement.installmentInfo,
    awsAccountId: get(agreement, 'resellersInfo[0].resellerAwsAccountId', '-'),
    companyName:
      agreement?.companyName ||
      get(agreement, 'resellersInfo[0].resellerCompanyName', '-'),
    metaData: agreement?.metaData,
    offerRecipients: agreement?.agreementRecipients,
    offerName: agreement?.agreementName,
    emailCustomMessage: agreement?.emailCustomMessage,
    eulaType: agreement?.endCustomerEulaType,
    eulaFile: agreement?.endCustomerEulaFiles,
    offerDescription: agreement?.agreementDescription,
    flexiblePaymentTerms:
      toUpper(agreement?.discountType) === DiscountType.FLEXIBLE_PAYMENT_TERMS,
    isCustomPricing: getIsCustomPricing(
      agreement?.discountType,
      agreement?.contractDurationType
    ),
    renewal: agreement?.renewal || false,
    renewalChannel: agreement?.renewalChannel || null,
    additionalCPPOData: [
      {
        label: 'Discount details',
        key: AdditionalCPPODataKeys.DISCOUNT_TYPE,
        value: agreement?.discountType,
      },
      {
        label: 'Discount details',
        key: AdditionalCPPODataKeys.DISCOUNT_PERCENTAGE,
        value: agreement?.percentageDiscount || '',
      },
      {
        label: 'Contract details',
        key: AdditionalCPPODataKeys.CONTRACT_DURATION_TYPE,
        value: isEmpty(agreement?.discountType)
          ? ''
          : agreement?.contractDurationType,
      },
      {
        label: 'Contract details',
        key: AdditionalCPPODataKeys.CONTRACT_DURATION_IN_MONTHS_FOR_END_CUSTOMER,
        value: agreement?.contractDurationInMonthsForEndCustomer || '',
      },
      {
        label: 'Reseller license agreement details',
        key: AdditionalCPPODataKeys.RESELLER_LICENSE_AGREEMENT_TYPE,
        value: agreement?.resellerLicenseAgreementType,
      },
      {
        label: 'Buyer details',
        key: AdditionalCPPODataKeys.BUYER_AWS_ACCOUNT_ID,
        value: agreement?.buyerAwsAccountId,
      },
      {
        label: 'Reseller license agreement details',
        key: AdditionalCPPODataKeys.RESELLER_LICENSE_AGREEMENT_FILES,
        value: agreement?.resellerLicenseAgreementFiles ?? [],
      },
      {
        label: 'Agreement duration',
        key: AdditionalCPPODataKeys.AGREEMENT_DURATION_TYPE,
        value: agreement.agreementDurationType,
      },
      {
        label: 'Agreement duration',
        key: AdditionalCPPODataKeys.AGREEMENT_EXPIRATION_DATE,
        value: !isEmpty(agreement?.agreementExpirationDate)
          ? agreementExpirationDate
          : 'N.A.',
      },
    ],
    resellerName: agreement?.resellerName,
    stakeholders: agreement?.agreementStakeholders,
  }
  return offer as DetailedOffer
}

export const getResellersDataBasedOnCloud = ({
  cloud,
  resellerAgreement,
}: {
  cloud: CloudMarketplace
  resellerAgreement: ResaleAuthorizationsResellerAgreements
}) => {
  switch (cloud) {
    case 'AWS':
      return {
        resellerName: get(
          resellerAgreement,
          'resellersInfo[0].resellerCompanyName',
          '-'
        ),
        status: resellerAgreement?.status,
        authorizationName: resellerAgreement?.agreementName,
        expiredAt: resellerAgreement?.agreementExpirationDate,
      }
    case 'AZURE':
      return {
        resellerName: get(
          resellerAgreement,
          'resellerDetails[0].partnerName',
          '-'
        ),
        authorizationName: resellerAgreement?.mppoName,
        status: resellerAgreement?.offerStatus,
        expiredAt: resellerAgreement?.acceptBy,
      }
    default:
      return {
        resellerName: '-',
        status: '',
        authorizationName: '-',
        expiredAt: '-',
      }
  }
}

export const getResaleMenuOptions = (
  agreementId: string,
  baseUrl: string,
  productId: string,
  draft: boolean,
  cloud?: CloudMarketplace,
  agreement?: ResaleAuthorizationsResellerAgreements,
  copiedResellerAuthorizationLink?: boolean,
  routeCloud?: string,
  accountId?: string,
  externalBaseURL?: string,
  secureKeyFromURL?: string,
  partnerIdFromURL?: string
) => {
  const draftUrl = `/resale-authorizations/${agreementId}/edit`
  const viewUrl = `/resale-authorizations/${agreementId}/details`

  let pathName: string
  if (externalBaseURL) {
    pathName =
      routeCloud && accountId
        ? `/external-apps/hubspot/v1/${secureKeyFromURL}/partners/${partnerIdFromURL}/cloud/${routeCloud}/accounts/${accountId}/product-listing/${productId}${draft ? draftUrl : viewUrl}`
        : `${baseUrl}/${productId}${draft ? draftUrl : viewUrl}`
  } else if (routeCloud && accountId) {
    pathName = `/flyout/${routeCloud}/${accountId}/products-listing/${productId}${draft ? draftUrl : viewUrl}`
  } else {
    pathName = `${baseUrl}/${productId}${draft ? draftUrl : viewUrl}`
  }
  const viewOption = {
    key: 'view-details',
    label: 'View details',
    disable: false,
    disabledTooltip: '',
    route: pathName,
  }
  const deleteOption = {
    key: 'delete',
    label: 'Delete authorization',
    disable: !(
      agreement?.draft === true ||
      agreement?.status === ResellerAgreementStatus.FAILED
    ),
    disabledTooltip: getDeleteResellerDisabledTooltip(agreement, cloud),
  }
  const microsotOptions = [
    { ...viewOption },
    { ...deleteOption },
    {
      key: 'copy-reseller-authorization-url',
      label: (
        <LabraToolTipV2
          description={`${copiedResellerAuthorizationLink ? 'Copied' : ''}`}
          tooltipPlacement="top"
          icon={
            <FontAwesomeIcon
              icon={faCheck}
              style={{ width: '12px', height: '12px' }}
            />
          }
          tooltipPlacmentGap="0"
        >
          Copy Reseller authorization URL
        </LabraToolTipV2>
      ),
      disable: !agreement?.acceptanceLink,
      disabledTooltip: `The Reseller authorization URL is not yet ready. It is in progress at ${Marketplace[cloud! as keyof typeof Marketplace]}.`,
      route: '',
    },
  ]

  return cloud === 'AZURE' ? microsotOptions : [viewOption, deleteOption]
}
